<template>
  <div class="booking-summary" :class="summaryClass" :style="cssVariables">
    <div v-if="businessName" class="summary-item stacked">
      <div class="summary-label">Uzņēmums:</div>
      <div class="summary-value">{{ businessName }}</div>
    </div>

    <div v-if="service" class="summary-item stacked">
      <div class="summary-label">Pakalpojums:</div>
      <div class="summary-value">{{ service.name }}</div>
    </div>

    <div v-if="selectedAddons && selectedAddons.length > 0" class="summary-item stacked">
      <div class="summary-label">Papildinājumi:</div>
      <div class="summary-value">{{ formatAddons(selectedAddons) }}</div>
    </div>

    <div v-if="provider && showProvider" class="summary-item stacked">
      <div class="summary-label">Speciālists:</div>
      <div class="summary-value">{{ provider.name }}</div>
    </div>
    
    <div v-if="date && timeSlot" class="summary-item stacked">
      <div class="summary-label">Datums un laiks:</div>
      <div class="summary-value">
        {{ formatDate(date) }} plkst. {{ formatTime(timeSlot.startTime) }}
      </div>
    </div>
    
    <div v-if="service && showDuration" class="summary-item stacked">
      <div class="summary-label">Ilgums:</div>
      <div class="summary-value">{{ formatDuration(totalDuration) }}</div>
    </div>

    <div v-if="totalPrice > 0" class="summary-item stacked">
      <div class="summary-label">Cena:</div>
      <div class="summary-value">{{ formatPrice(totalPrice) }}</div>
    </div>
    
    <div v-if="showClientInfo && clientInfo.email" class="summary-item stacked">
      <div class="summary-label">E-pasts:</div>
      <div class="summary-value">{{ clientInfo.email }}</div>
    </div>
    
    <div v-if="showClientInfo && clientInfo.phone" class="summary-item stacked">
      <div class="summary-label">Telefona numurs:</div>
      <div class="summary-value">{{ clientInfo.phone }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BookingSummary',
  props: {
    businessName: {
      type: String,
      default: null
    },
    service: {
      type: Object,
      default: null
    },
    selectedAddons: {
      type: Array,
      default: () => []
    },
    provider: {
      type: Object,
      default: null
    },
    date: {
      type: Date,
      default: null
    },
    timeSlot: {
      type: Object,
      default: null
    },
    clientInfo: {
      type: Object,
      default: () => ({
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      })
    },
    showProvider: {
      type: Boolean,
      default: true
    },
    showDuration: {
      type: Boolean,
      default: false
    },
    showClientInfo: {
      type: Boolean,
      default: false
    },
    summaryClass: {
      type: String,
      default: ''
    },
    primaryLight: {
      type: String,
      default: null
    }
  },
  computed: {
    cssVariables() {
    if (this.primaryLight) {
        return {
          '--primary-light': this.primaryLight
        };
      }
      return {};
    },
    totalDuration() {
      let total = 0;

      if (this.service && this.service.duration) {
        total += this.service.duration;
      }

      if (this.selectedAddons && this.selectedAddons.length > 0) {
        this.selectedAddons.forEach(addon => {
          if (addon.duration) {
            total += addon.duration;
          }
        });
      }

      return total;
    },
    totalPrice() {
      let total = 0;

      // Add service price if it exists
      if (this.service && this.service.price) {
        total += this.service.price;
      }

      // Add all selected addon prices
      if (this.selectedAddons && this.selectedAddons.length > 0) {
        this.selectedAddons.forEach(addon => {
          if (addon.price) {
            total += addon.price;
          }
        });
      }

      return total;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const currentYear = new Date().getFullYear();
        const dateYear = date.getFullYear();

        const weekday = date.toLocaleDateString('lv-LV', { weekday: 'long' });
        const day = date.getDate();
        const month = date.toLocaleDateString('lv-LV', { month: 'long' });

        const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);

        if (dateYear === currentYear) {
          return `${capitalizedWeekday}, ${day}. ${month}`;
        } else {
          return `${capitalizedWeekday}, ${day}. ${month} ${dateYear}`;
        }
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    },
    formatTime(timeString) {
      if (!timeString) return '';
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    },
    formatPrice(price) {
      return `€${price.toFixed(2)}`;
    },
    formatDuration(minutes) {
      if (minutes < 60) {
        return `${minutes}min`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return hours === 1 ? '1 stunda' : `${hours} stundas`;
      }
      const hourLabel = hours === 1 ? '1 stunda' : `${hours} stundas`;
      return `${hourLabel} ${remainingMinutes} minūtes`;
    },
    formatAddons(addons) {
      if (!addons || addons.length === 0) return '';
      return addons.map(addon => addon.name).join(', ');
    }
  }
}
</script>

<style scoped>
.booking-summary {
  background-color: var(--primary-light, #F7F3F7);
  border-radius: 20px;
  border: none;
  padding: 32px;
}

.summary-item {
  display: flex;
  margin-bottom: 10px;
  font-size: 15px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-item:last-child,
.summary-item.stacked:last-child {
  margin-bottom: 0 !important;
}

.summary-label {
  width: 120px;
  font-weight: 400;
  color: var(--text-color-secondary);
  font-size: 14px;
}

.summary-value {
  flex: 1;
  font-size: 16px;
}

@media (max-width: 480px) {
  .booking-summary {
    padding: 1rem;
    border-radius: 16px;
  }

  .summary-item.stacked {
    flex-direction: column;
    margin-bottom: 15px;
  }

  .summary-item.stacked .summary-label {
    width: auto;
    margin-bottom: 5px;
  }
}
</style>